/**
 * Sessionizzazione delle chat WhatsApp per la cronologia cliente (vista 7).
 *
 * Regola di spezzatura DETERMINISTICA e CONSAPEVOLE DELLA DIREZIONE (design
 * congelato 12 ago, MD/features/2026-08-10-drawer-note-ui-ux-redesign-proposta.md):
 * il solo gap temporale taglierebbe male "domanda la sera, risposta la mattina".
 *
 * 1. Scambio aperto = sessione aperta: se l'ultimo messaggio attende ancora una
 *    risposta dell'altra parte, la sessione resta aperta fino a 24 ore — la
 *    risposta del mattino (o il sollecito dello stesso mittente) finisce nella
 *    STESSA sessione della domanda della sera.
 * 2. Scambio chiuso + silenzio: dopo che c'è stata risposta, una pausa di 3+ ore
 *    chiude la sessione; il messaggio successivo apre una sessione nuova.
 * 3. Tetto assoluto: 24 ore di silenzio chiudono comunque, anche senza risposta.
 * 4. MAI per argomento (in fase 2 l'AI darà un TITOLO alla sessione, non un taglio).
 *
 * Implementazione: macchina a stati `awaiting` = direzione di chi ha parlato per
 * ultimo e attende risposta (null = lo scambio è stato risposto e nessuno ha
 * rilanciato). Un messaggio dalla direzione opposta a `awaiting` è la risposta
 * attesa e azzera lo stato; un messaggio con `awaiting` null (o della stessa
 * direzione) apre/rinnova l'attesa della controparte.
 *
 * Soglie COSTANTI nel codice, non impostazioni (scelta di design).
 */

/** Dopo una risposta, 3 ore di silenzio chiudono la sessione. */
export const SESSION_ANSWERED_GAP_MS = 3 * 60 * 60 * 1000;
/** Tetto assoluto: 24 ore di silenzio chiudono sempre. */
export const SESSION_MAX_GAP_MS = 24 * 60 * 60 * 1000;

export interface SessionizableMessage {
  direction: 'inbound' | 'outbound';
  /** Timestamp del messaggio (Date o epoch ms). */
  timestamp: Date | number;
}

export interface WhatsAppSessionGroup<T extends SessionizableMessage> {
  /** Messaggi della sessione in ordine cronologico ascendente. */
  messages: T[];
  startAt: Date;
  endAt: Date;
}

function toMs(value: Date | number): number {
  return value instanceof Date ? value.getTime() : value;
}

/**
 * Divide i messaggi di UNA chat in sessioni di conversazione.
 * `messages` può arrivare in qualsiasi ordine: viene ordinato asc internamente.
 */
export function sessionizeMessages<T extends SessionizableMessage>(
  messages: T[],
): WhatsAppSessionGroup<T>[] {
  if (messages.length === 0) return [];

  const sorted = [...messages].sort((a, b) => toMs(a.timestamp) - toMs(b.timestamp));
  const sessions: WhatsAppSessionGroup<T>[] = [];

  let current: T[] = [];
  // Direzione che ha parlato per ultima e attende risposta; null = scambio risposto.
  let awaiting: 'inbound' | 'outbound' | null = null;

  const closeCurrent = () => {
    if (current.length === 0) return;
    sessions.push({
      messages: current,
      startAt: new Date(toMs(current[0].timestamp)),
      endAt: new Date(toMs(current[current.length - 1].timestamp)),
    });
    current = [];
    awaiting = null;
  };

  for (const msg of sorted) {
    if (current.length > 0) {
      const gap = toMs(msg.timestamp) - toMs(current[current.length - 1].timestamp);
      const answered = awaiting === null;
      if (gap >= SESSION_MAX_GAP_MS || (answered && gap >= SESSION_ANSWERED_GAP_MS)) {
        closeCurrent();
      }
    }

    current.push(msg);
    if (awaiting === null || awaiting === msg.direction) {
      // Apre (o rinnova con un sollecito) l'attesa della controparte.
      awaiting = msg.direction;
    } else {
      // È la risposta attesa: lo scambio si chiude.
      awaiting = null;
    }
  }

  closeCurrent();
  return sessions;
}
